import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress } from '@mui/material';
import packages, { apiError, errorMessage } from 'common/packages';
import NotPackageManager, { usePackageManager } from 'components/NotPackageManager';
import { UploadForm, UploadProgress } from './components';

const PackageUpload = () => {
  const { application } = useParams();
  const navigate = useNavigate();
  const { isManager, error: permissionError } = usePackageManager();

  const [running, setRunning] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [error, setError] = useState(null);
  const [detailPath, setDetailPath] = useState(null);

  const mark = (key, changes) =>
    setUploads((previous) =>
      previous.map((upload) => (upload.key === key ? { ...upload, ...changes } : upload))
    );

  const handleSubmit = async (values) => {
    const artifactContentType = values.file.type || 'application/octet-stream';

    const steps = [
      { key: 'register', label: 'Registering the package', progress: null, status: 'in progress' },
      { key: 'file', label: `Artifact — ${values.file.name}`, progress: 0, status: 'waiting' },
    ];
    if (values.thumbnail) {
      steps.push({ key: 'thumbnail', label: 'Thumbnail', progress: 0, status: 'waiting' });
    }
    if (values.icon) {
      steps.push({ key: 'icon', label: 'Icon', progress: 0, status: 'waiting' });
    }

    setRunning(true);
    setError(null);
    setDetailPath(null);
    setUploads(steps);

    const form = new FormData();
    form.append('fileName', values.file.name);
    form.append('packageName', values.packageName.trim());
    form.append('version', values.version.trim());
    form.append('type', values.type.trim());
    // The artifact's presigned PUT URL is signed for this content type; the PUT
    // below must send exactly the same one or S3 answers SignatureDoesNotMatch.
    form.append('contentType', artifactContentType);
    form.append('size', String(values.file.size));
    form.append('isPublic', String(values.isPublic));
    form.append('isVisible', String(values.isVisible));
    if (values.label.trim()) form.append('label', values.label.trim());
    if (values.description.trim()) form.append('description', values.description.trim());
    if (values.compatibility.trim()) form.append('compatibility', values.compatibility.trim());
    if (values.manifest.trim()) form.append('manifest', values.manifest.trim());
    if (values.signature.trim()) form.append('signature', values.signature.trim());

    let registered;
    try {
      registered = await packages.register(application, form);
    } catch (e) {
      const failure = apiError(e);
      mark('register', { status: 'failed', failed: true, progress: 100 });
      setError({ code: failure.code, message: errorMessage(failure) });
      setRunning(false);
      return;
    }

    mark('register', { status: 'done', progress: 100 });

    const uuid = registered.package.uuid;
    const path = `/packages/${application}/${uuid}`;
    // Set before the first PUT: from here on the row exists, so every failure is
    // recoverable from the detail page rather than by starting over.
    setDetailPath(path);

    const put = async (key, url, file, contentType) => {
      mark(key, { status: 'uploading' });
      await packages.putToS3(url, file, {
        contentType,
        onProgress: (progress) => mark(key, { progress }),
      });
      mark(key, { status: 'done', progress: 100 });
    };

    try {
      await put('file', registered.uploadUrls.file, values.file, artifactContentType);
      // Thumbnail and icon URLs are signed for image/png specifically.
      if (values.thumbnail) {
        await put('thumbnail', registered.uploadUrls.thumbnail, values.thumbnail, 'image/png');
      }
      if (values.icon) {
        await put('icon', registered.uploadUrls.icon, values.icon, 'image/png');
      }
    } catch (e) {
      const failure = apiError(e);
      setError({ message: `Upload to storage failed: ${failure.message}` });
      setRunning(false);
      return;
    }

    navigate(path, { replace: true });
  };

  if (permissionError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Could not check package-management permissions: {permissionError.message}
        </Alert>
      </Box>
    );
  }

  if (isManager === null) {
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (isManager === false) {
    return (
      <Box sx={{ p: 4 }}>
        <NotPackageManager />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {uploads.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <UploadProgress uploads={uploads} error={error} detailPath={detailPath} />
        </Box>
      )}
      <UploadForm application={application} submitting={running} onSubmit={handleSubmit} />
    </Box>
  );
};

export default PackageUpload;
